"use client";
import Image from "next/image";
import { useTranslations } from "next-intl";
import type { SiteSettings } from "@/types/settings";
import TransitionLink from "./transitionLink";
import MarketingSvg from "./svg/marketing";
import FilmSvg from "./svg/film";
import PicturesSvg from "./svg/pictures";
import AutoContactLine from "./autoContactLine";

interface FooterProps {
  data?: SiteSettings;
  year: number;
}

export default function Footer({ data, year }: FooterProps) {
  const t = useTranslations("footer");
  const settings = data?.footerSettings;
  const contactText = settings?.contacts ?? [];
  return (
    <footer className="grid gap-16 mt-4 container mx-auto">
      {/* Loga grid - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TransitionLink
          title="tmrw.marketing"
          href="/"
          classes="logo-link frame hover:bg-neutral-50 transition-all duration-200 hoverable flex justify-center items-center px-4 py-12"
        >
          <MarketingSvg />
        </TransitionLink>
        {settings?.links?.film?.isVisible && (
          <a
            href={settings.links.film.url ?? "/"}
            target="_blank"
            title="tmrw.film"
            rel="noopener noreferrer"
            className="logo-link frame hover:bg-neutral-50 transition-all duration-200 hoverable flex justify-center items-center px-4 py-12"
          >
            <FilmSvg />
          </a>
        )}
        {settings?.links?.pictures?.isVisible && (
          <a
            href={settings?.links?.pictures?.url ?? "/"}
            target="_blank"
            rel="noopener noreferrer"
            title="tmrw.pictures"
            className="logo-link frame hover:bg-neutral-50 transition-all duration-200 hoverable flex justify-center items-center px-4 py-12"
          >
            <PicturesSvg />
          </a>
        )}
      </div>
      <div className="grid xl:grid-cols-2 gap-y-12 gap-x-4 pb-4">
        <div className="grid xl:justify-between gap-8 xl:gap-12">
          <div className="h3 uppercase max-w-xl">{settings?.text}</div>
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-4"
            role="group"
            aria-label="Partneři"
          >
            <a
              href="https://stape.io"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Stape partner"
              className="inline-flex h-[72px] items-center justify-center transition-opacity hover:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
            >
              <Image
                src="https://cdn.stape.io/i/644286f038012250033022.svg"
                alt="Stape partner"
                width={130}
                height={62}
                className="h-[62px] w-[130px] object-contain"
                loading="lazy"
                unoptimized
              />
            </a>
            <a
              href="https://www.google.com/partners/agency?id=4215559494"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Google Partner"
              className="inline-flex h-[72px] items-center justify-center transition-opacity hover:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
            >
              <Image
                src="https://www.gstatic.com/partners/badge/images/2026/PartnerBadgeClickable.svg"
                alt="Google Partner"
                width={65}
                height={62}
                className="h-[62px] w-[65px] object-contain"
                loading="lazy"
                unoptimized
              />
            </a>
          </div>
        </div>
        <div className="flex flex-col justify-between gap-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full text-center sm:text-left">
            {settings?.menu && settings.menu.length > 0 && (
              <div>
                <span className="mono opacity-60 mb-2">{t("menu")}</span>
                <div className="flex flex-col items-center sm:items-start">
                  {settings.menu.map((item: any) => (
                    <TransitionLink
                      href={`/${item.page.slug.current}`}
                      classes="text-base hover:opacity-50 transition-all duration-200 py-2 inline-block"
                      key={item.page.slug.current}
                    >
                      {item.title}
                    </TransitionLink>
                  ))}
                </div>
              </div>
            )}
            {settings?.services && settings.services.length > 0 && (
              <div>
                <span className="mono opacity-60 mb-2">{t("services")}</span>
                <div className="flex flex-col items-center sm:items-start">
                  {settings.services?.map((item: any) => (
                    <TransitionLink
                      href={`/${item.page.slug.current}`}
                      classes="text-base hover:opacity-50 transition-all duration-200 py-2 inline-block"
                      key={item.page.slug.current}
                    >
                      {item.title}
                    </TransitionLink>
                  ))}
                </div>
              </div>
            )}
            {settings?.otherServices && settings.otherServices.length > 0 && (
              <div>
                <span className="mono opacity-60 mb-2">{t("otherServices")}</span>
                <div className="flex flex-col items-center sm:items-start">
                  {settings.otherServices.map((item: any) => (
                    <TransitionLink
                      href={`/${item.page.slug.current}`}
                      classes="text-base hover:opacity-50 transition-all duration-200 py-2 inline-block"
                      key={item.page.slug.current}
                    >
                      {item.title}
                    </TransitionLink>
                  ))}
                </div>
              </div>
            )}
            {contactText.length > 0 && (
              <div>
                <span className="mono opacity-60 mb-2">{t("contact")}</span>
                <div className="text-pretty text-base">
                  {contactText.map((line, i) => (
                    <AutoContactLine key={i} line={line} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2">
            <span className="mono">©{year} tmrw.</span>
            <span className="mono">·</span>
            <span className="mono">{t("allRightsReserved")}</span>
            <span className="mono">·</span>
            <a
              href="#"
              className="cky-banner-element mono hover:opacity-50 transition-opacity cursor-pointer"
            >
              {t("cookieSettings")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
